import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";
import { Watchlist } from "./useWatchlists";

interface RenameWatchlistData {
  watchlistId: string;
  name: string;
}

interface RenameWatchlistResponse {
  data: Watchlist;
}

async function renameWatchlist({ watchlistId, name }: RenameWatchlistData): Promise<Watchlist> {
  try {
    const { data } = await api.patch<RenameWatchlistResponse>(
      `/stock-watchlists/${watchlistId}`,
      { name },
    );
    return data.data;
  } catch (error: any) {
    // Duplicate names and validation failures come back as a 400 with a
    // human-readable message — surface it verbatim so the modal can show it.
    throw new Error(
      error.response?.data?.error ??
        `Failed to rename watchlist: ${error.response?.status ?? "Unknown error"}`,
    );
  }
}

// Deliberately not optimistic: this is a single string behind a modal that
// already shows a pending state, so optimism would only buy a rollback path.
export function useRenameWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: renameWatchlist,
    onSuccess: (renamed) => {
      queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      queryClient.invalidateQueries({ queryKey: ["watchlist", renamed.id] });
    },
  });
}
